import React, { useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { useQuery, useQueryClient } from "react-query";
// useQuery is used for businessLocationData and menus; useQueryClient for cache invalidation
import { setShowSubscriptionPlanDialog } from "src/redux/slices/AuthSlice";
import { fetchBusinessSubscription } from "src/redux/actions/AuthAction";
import UpgradePlanDialog from "src/@core/components/UpgradePlanDialog";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  CircularProgress,
} from "@mui/material";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import StepContainer from "./components/StepContainer";
import MethodSelection from "./components/MethodSelection";
import UploadSelection from "./components/UploadSelection";
import ProcessingScreen from "./components/ProcessingScreen";
import ConfirmMenu from "./components/ConfirmMenu";
import { transformOcrResponse, transformOcrToMenuPayload, generateItemCode } from "./ocrUtils";

const OCR_API_URL = `${process.env.REACT_APP_AI_URL}/ocr-menu`;

/**
 * MenuOcrFlow - Main orchestrator for the AI Menu OCR step-based flow
 *
 * Steps:
 *  1 → MethodSelection (AI vs Manual) — also shows saved drafts
 *  2 → UploadSelection (Images vs PDF) — opens native file picker
 *  3 → ProcessingScreen (real OCR API call with animated progress)
 *  4 → ConfirmMenu (review, select, confirm/save-draft)
 *
 * @param {Function} onClose        - Close/exit the flow
 * @param {Function} onManualEntry  - Handler to open existing manual menu creation
 *                                    Called with (draftMenu?) when continuing a draft
 */
const MenuOcrFlow = ({ onClose, onManualEntry, isCatalog = false }) => {
  const [step, setStep] = useState(1);
  const [parsedMenuData, setParsedMenuData] = useState(null);

  // API state for ProcessingScreen
  const [isApiDone, setIsApiDone] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Submission loading state: null | "draft" | "confirm"
  const [isSubmitting, setIsSubmitting] = useState(null);

  // Upgrade dialog state
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [upgradeDialogConfig, setUpgradeDialogConfig] = useState({
    featureType: "categories",
    currentQuantity: 0,
    maxQuantity: 0,
  });

  // Location picker dialog state (shown before saving draft or confirming menu)
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [pendingConfirmData, setPendingConfirmData] = useState(null);
  const [pendingIsDraft, setPendingIsDraft] = useState(false);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);

  // Abort controller ref for cancelling in-flight OCR requests
  const abortRef = useRef(null);

  const dispatch = useDispatch();
  const selectedBusinessId = useSelector(
    (state) => state.auth.selectedBusinessId
  );
  const selectedBusiness = useSelector((state) => state.auth.selectedBusiness);
  // Business subscription info is stored in Redux by businessId
  const businessSubscriptions = useSelector(
    (state) => state.auth.businessSubscriptions || {}
  );
  const queryClient = useQueryClient();

  // ── Fetch business location (currency + location list for picker) ────────
  const { data: businessLocationData } = useQuery(
    ["businessLocationData", selectedBusinessId],
    async ({ signal }) => {
      const res = await axiosInstance.get(
        `${ApiEndPoints.BUSINESS_LOCATION.getAll}/${selectedBusinessId}`,
        { signal }
      );
      return res.data.data;
    },
    { enabled: !!selectedBusinessId }
  );

  // Get currency symbol from first business location (fallback to "$")
  const currencySymbol =
    businessLocationData?.locations?.[0]?.currencySymbol || "$";

  // ── Business subscription plan limits (from Redux, fetched via thunk) ───────
  // Fetch subscription info if not already in Redux
  const subscriptionInfo = businessSubscriptions[selectedBusinessId];
  React.useEffect(() => {
    if (selectedBusinessId && !subscriptionInfo) {
      dispatch(fetchBusinessSubscription(selectedBusinessId));
    }
  }, [selectedBusinessId, subscriptionInfo, dispatch]);

  // Extract plan limits from subscriptionPlan features
  const planLimits = useMemo(() => {
    if (!subscriptionInfo) return null;
    const features = subscriptionInfo?.subscriptionPlan?.features || [];
    const catFeature = features.find((f) => f.featureKey === "categories");
    const itemFeature = features.find((f) => f.featureKey === "items_per_menu");
    return {
      maxCategories:
        catFeature && catFeature.enabled && !catFeature.isUnlimited
          ? catFeature.quantity
          : null,
      maxItems:
        itemFeature && itemFeature.enabled && !itemFeature.isUnlimited
          ? itemFeature.quantity
          : null,
    };
  }, [subscriptionInfo]);

  // ── Fetch existing category count (to enforce plan limits against DB data) ──
  const { data: existingCategoriesData } = useQuery(
    ["menuCategory", selectedBusinessId, isCatalog],
    async ({ signal }) => {
      const res = await axiosInstance.get(
        `${ApiEndPoints.MENU_CATEGORY.get}/${selectedBusinessId}`,
        { signal, params: { isCatalog } }
      );
      return res.data.data;
    },
    { enabled: !!selectedBusinessId, staleTime: 0, refetchOnWindowFocus: false }
  );

  // ── Fetch existing item count ─────────────────────────────────────────────
  const { data: existingItemsData } = useQuery(
    ["menuItems", selectedBusinessId, isCatalog],
    async ({ signal }) => {
      const res = await axiosInstance.get(
        `${ApiEndPoints.MENU_ITEM.get}/${selectedBusinessId}`,
        { signal, params: { isCatalog } }
      );
      return res.data.data;
    },
    { enabled: !!selectedBusinessId, staleTime: 0, refetchOnWindowFocus: false }
  );

  const existingCategoryCount = existingCategoriesData?.categories?.length || 0;
  const existingItemCount = existingItemsData?.items?.length || 0;

  // ── Fetch all menus (filter draft: true for draft list) ──────────────────
  const { data: menusData, refetch: refetchMenus } = useQuery(
    ["menus", selectedBusinessId],
    async () => {
      const res = await axiosInstance.get(
        `${ApiEndPoints.MENU.get}/${selectedBusinessId}`
      );
      return res.data;
    },
    { enabled: !!selectedBusinessId && step === 1 }
  );

  const draftMenus = useMemo(
    () =>
      (
        menusData?.data?.menus ||
        menusData?.menus ||
        menusData?.data ||
        []
      ).filter((m) => m.draft === true),
    [menusData]
  );

  // ── API call sequence: items → categories → menu ─────────────────────────
  const submitMenuSequence = useCallback(
    async ({ selectedCategoryIds, selectedItemIds, menuName, categories, locationIds, isDraft }) => {
      const { itemPayloads, categoryPayloads } = transformOcrToMenuPayload(
        { menuName, categories },
        selectedCategoryIds,
        selectedItemIds
      );

      // 1. Create all items in parallel
      const createdItems = await Promise.all(
        itemPayloads.map((item) =>
          axiosInstance
            .post(`${ApiEndPoints.MENU_ITEM.add}/${selectedBusinessId}`, {
              name: item.name,
              description: item.description,
              price: item.price,
              code: generateItemCode(),
              isCatalog,
            })
            .then((res) => ({
              _localId: item._localId,
              _id:
                res.data?.data?.item?._id ||
                res.data?.data?.menuItem?._id ||
                res.data?.data?._id ||
                res.data?._id,
            }))
        )
      );

      // Build local → DB ID map
      const itemIdMap = Object.fromEntries(
        createdItems.map((i) => [i._localId, i._id])
      );

      // 2. Create all categories in parallel (with resolved item IDs)
      const createdCategories = await Promise.all(
        categoryPayloads.map((cat) =>
          axiosInstance
            .post(`${ApiEndPoints.MENU_CATEGORY.add}/${selectedBusinessId}`, {
              name: cat.name,
              menuItemIds: cat._localItemIds
                .map((lid) => itemIdMap[lid])
                .filter(Boolean),
              isCatalog,
            })
            .then((res) => ({
              _localCatId: cat._localCatId,
              _id: res.data?.data?.category?._id || res.data?.data?._id || res.data?._id,
            }))
        )
      );

      const categoryIds = createdCategories.map((c) => c._id).filter(Boolean);

      // 3. Create menu
      await axiosInstance.post(
        `${ApiEndPoints.MENU.add}/${selectedBusinessId}`,
        {
          name: menuName,
          locationIds: locationIds || [],
          categories: categoryIds,
          isCatalog,
          ...(isDraft && { draft: true }),
        }
      );
    },
    [selectedBusinessId]
  );

  // ── Step 1 handlers ──────────────────────────────────────────────────────
  const handleSelectAI = useCallback(() => {
    setStep(2);
  }, []);

  const handleSelectManual = useCallback(() => {
    onManualEntry?.();
  }, [onManualEntry]);

  // ── Draft list handlers ──────────────────────────────────────────────────
  const handleContinueDraft = useCallback(
    (draft) => {
      // Open the manual edit flow with the existing draft menu
      onManualEntry?.(draft);
    },
    [onManualEntry]
  );

  const handleDeleteDraft = useCallback(
    async (menuId) => {
      try {
        await axiosInstance.delete(`${ApiEndPoints.MENU.add}/${menuId}`);
        refetchMenus();
        toast.success("draft deleted");
      } catch {
        toast.error("failed to delete draft");
      }
    },
    [refetchMenus]
  );

  // ── Step 2 handler ───────────────────────────────────────────────────────
  const handleFileSelected = useCallback((file) => {
    setIsApiDone(false);
    setApiError(null);
    setParsedMenuData(null);
    setStep(3);

    const controller = new AbortController();
    abortRef.current = controller;

    const formData = new FormData();
    formData.append("file", file);

    axios
      .post(OCR_API_URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        signal: controller.signal,
      })
      .then((res) => {
        const apiData = res.data;

        if (apiData.status === "error") {
          setApiError(apiData.message || "failed to parse menu data");
          return;
        }

        // Transform API response → UI format
        const transformed = transformOcrResponse(apiData);
        setParsedMenuData(transformed);
        setIsApiDone(true);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        const raw =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          "";
        let msg;
        if (
          raw.includes("unsupported image") ||
          raw.includes("invalid_image_format")
        ) {
          msg =
            "unsupported file format. please upload a png, jpeg, or webp image.";
        } else if (
          raw.includes("Only image") ||
          raw.includes("PDF files are allowed")
        ) {
          msg = "only image (jpeg, png, webp) or pdf files are allowed.";
        } else if (raw.includes("Failed to parse")) {
          msg =
            "could not read menu from this file. please try a clearer image.";
        } else {
          msg =
            "something went wrong while processing your menu. please try again.";
        }
        setApiError(msg);
      });
  }, []);

  // ── Step 3 handlers ──────────────────────────────────────────────────────
  const handleProcessingComplete = useCallback(() => {
    setStep(4);
  }, []);

  const handleProcessingError = useCallback((errorMsg) => {
    toast.error(errorMsg || "ocr processing failed");
    setStep(2);
    // setSelectedFile(null);
    setIsApiDone(false);
    setApiError(null);
  }, []);

  // ── Step 4 handlers ──────────────────────────────────────────────────────

  // Save as draft — show location picker first
  const handleSaveDraft = useCallback((selectionData) => {
    setPendingConfirmData(selectionData);
    setPendingIsDraft(true);
    setSelectedLocationIds([]);
    setLocationPickerOpen(true);
  }, []);

  // Confirm menu — show location picker first
  const handleConfirmMenu = useCallback((selectionData) => {
    setPendingConfirmData(selectionData);
    setPendingIsDraft(false);
    setSelectedLocationIds([]);
    setLocationPickerOpen(true);
  }, []);

  // After user picks locations, submit (draft or final)
  const handleLocationConfirm = useCallback(async () => {
    if (!pendingConfirmData) return;
    setLocationPickerOpen(false);
    setIsSubmitting(pendingIsDraft ? "draft" : "confirm");
    try {
      await submitMenuSequence({
        ...pendingConfirmData,
        locationIds: selectedLocationIds,
        isDraft: pendingIsDraft,
      });
      toast.success(
        pendingIsDraft
          ? `${isCatalog ? "catalogue" : "menu"} draft saved`
          : `${isCatalog ? "catalogue" : "menu"} created!`
      );
      refetchMenus();
      queryClient.invalidateQueries(["menus", selectedBusinessId]);
      onClose?.();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          (pendingIsDraft ? "failed to save draft" : "failed to create menu")
      );
    } finally {
      setIsSubmitting(null);
    }
  }, [
    pendingConfirmData,
    pendingIsDraft,
    selectedLocationIds,
    isCatalog,
    submitMenuSequence,
    refetchMenus,
    queryClient,
    selectedBusinessId,
    onClose,
  ]);

  // ── Navigation ───────────────────────────────────────────────────────────
  const getDisplayStep = () => {
    if (step <= 2) return step;
    if (step === 3) return 2; // processing is part of step 2
    return 3; // confirmation is step 3
  };

  const showHeader = step !== 3;

  const handleBack = useCallback(() => {
    if (step === 2) {
      setStep(1);
    } else if (step === 4) {
      abortRef.current?.abort();
      setParsedMenuData(null);
      setIsApiDone(false);
      setApiError(null);
      setStep(2);
    }
  }, [step]);

  const canGoBack = step === 2 || step === 4;

  return (
    <>
      <StepContainer
        currentStep={getDisplayStep()}
        totalSteps={3}
        onClose={() => {
          abortRef.current?.abort();
          onClose?.();
        }}
        onBack={canGoBack ? handleBack : undefined}
        showHeader={showHeader}
      >
        {/* Step 1: Method Selection + draft list */}
        {step === 1 && (
          <MethodSelection
            isCatalog={isCatalog}
            onSelectAI={handleSelectAI}
            onSelectManual={handleSelectManual}
            drafts={draftMenus}
            onContinueDraft={handleContinueDraft}
            onDeleteDraft={handleDeleteDraft}
          />
        )}

        {/* Step 2: Upload Selection */}
        {step === 2 && (
          <UploadSelection onFileSelected={handleFileSelected} />
        )}

        {/* Step 3: Processing (real OCR API) */}
        {step === 3 && (
          <ProcessingScreen
            isApiDone={isApiDone}
            apiError={apiError}
            onComplete={handleProcessingComplete}
            onError={handleProcessingError}
          />
        )}

        {/* Step 4: Confirm Menu / Catalogue */}
        {step === 4 && parsedMenuData && (
          <ConfirmMenu
            isCatalog={isCatalog}
            menuData={parsedMenuData}
            currencySymbol={currencySymbol}
            planLimits={planLimits}
            existingCategoryCount={existingCategoryCount}
            existingItemCount={existingItemCount}
            isSubmitting={isSubmitting}
            onSaveDraft={handleSaveDraft}
            onConfirm={handleConfirmMenu}
            onUpgrade={(currentCount, featureType = "categories") => {
              const isItems = featureType === "items";
              setUpgradeDialogConfig({
                featureType: isItems ? "items" : "categories",
                currentQuantity: currentCount ?? 0,
                maxQuantity: isItems
                  ? planLimits?.maxItems ?? 0
                  : planLimits?.maxCategories ?? 0,
              });
              setShowUpgradeDialog(true);
            }}
          />
        )}
      </StepContainer>

      {/* ── Location picker dialog (shown before confirming menu) ─────────── */}
      <Dialog
        open={locationPickerOpen}
        onClose={() => setLocationPickerOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3, minWidth: { xs: 300, sm: 400 } } } }}
      >
        <DialogTitle
          sx={{ fontWeight: 700, fontSize: 16, textTransform: "lowercase" }}
        >
          {pendingIsDraft ? "save draft to location" : "select location"}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ color: "#6b7280", mb: 2, textTransform: "lowercase" }}
          >
            which location will this {isCatalog ? "catalogue" : "menu"}{pendingIsDraft ? " draft" : ""} be available at?
          </Typography>
          {(businessLocationData?.locations || []).map((loc) => (
            <FormControlLabel
              key={loc._id}
              control={
                <Checkbox
                  checked={selectedLocationIds.includes(loc._id)}
                  onChange={(e) =>
                    setSelectedLocationIds((prev) =>
                      e.target.checked
                        ? [...prev, loc._id]
                        : prev.filter((id) => id !== loc._id)
                    )
                  }
                  sx={{
                    color: "#7C3AED",
                    "&.Mui-checked": { color: "#7C3AED" },
                  }}
                />
              }
              label={
                <Typography
                  sx={{ fontSize: "0.9rem", textTransform: "lowercase" }}
                >
                  {loc.businessLocationName}
                </Typography>
              }
              sx={{ display: "flex", mb: 0.5 }}
            />
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => setLocationPickerOpen(false)}
            sx={{
              bgcolor: "#7C3AED",
              color: "#fff",
              textTransform: "lowercase",
              borderRadius: 2,
              "&:hover": { bgcolor: "#6200EE" },
            }}
          >
            cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleLocationConfirm}
            disabled={selectedLocationIds.length === 0 || !!isSubmitting}
            sx={{
              bgcolor: "#7C3AED",
              textTransform: "lowercase",
              borderRadius: 2,
              "&:hover": { bgcolor: "#6200EE" },
              "&.Mui-disabled": { bgcolor: "#E9E3FF", color: "#9CA3AF" },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : pendingIsDraft ? (
              "save draft"
            ) : (
              "confirm"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Upgrade plan dialog ───────────────────────────────────────────── */}
      <UpgradePlanDialog
        open={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        selectedBusiness={selectedBusiness}
        featureType={upgradeDialogConfig.featureType}
        currentQuantity={upgradeDialogConfig.currentQuantity}
        maxQuantity={upgradeDialogConfig.maxQuantity}
        onUpgrade={() => {
          dispatch(
            setShowSubscriptionPlanDialog({
              show: true,
              businessId: selectedBusinessId,
              currentPlan: selectedBusiness?.planName,
            })
          );
        }}
      />
    </>
  );
};

export default MenuOcrFlow;
